import client from '../graphql/client'
import { CURRENT_USER } from '../graphql/query/me'
import { UPDATE_USER } from '../graphql/mutation/user'

export const APPSUMO_REVIEW_KEY = 'appsumo_review'
export const SNOOZE_DAYS = 7

export const getAppsumoReview = (data = {}) => data?.[APPSUMO_REVIEW_KEY] || {}

export const mergeAppsumoReview = (currentData = {}, patch = {}) => ({
  ...getAppsumoReview(currentData),
  ...patch,
  updated_at: new Date().toISOString(),
})

export const DONE_STATUSES = ['posted', 'dismissed']

export const isAppsumoReviewDismissed = (review = {}) => DONE_STATUSES.includes(review?.status)

export const isAppsumoReviewSnoozed = (review = {}) => {
  if (!review?.snoozed_until) return false
  const until = new Date(review.snoozed_until).getTime()
  return Number.isFinite(until) && until > Date.now()
}

export const hasAppsumoGotValue = (review = {}) => !!review?.got_value

export const hasAppsumoPendingWizard = (review = {}) => !!review?.pending_wizard

export const isEligibleAppsumoLicense = (entitlements = {}) => {
  const appsumoUser = !!(entitlements?.appsumoUser || entitlements?.appsumo_user)
  const everDowngraded = !!(entitlements?.appsumoEverDowngraded || entitlements?.appsumo_ever_downgraded)
  return appsumoUser && !everDowngraded
}

export const computeShouldShow = ({ entitlements, data } = {}) => {
  if (!isEligibleAppsumoLicense(entitlements || window.entitlements || {})) return false
  const review = getAppsumoReview(data)
  if (!hasAppsumoGotValue(review)) return false
  if (isAppsumoReviewDismissed(review)) return false
  if (isAppsumoReviewSnoozed(review)) return false
  return true
}

export const shouldMarkGotValueAfterSend = ({ entitlements, data } = {}) => {
  if (!isEligibleAppsumoLicense(entitlements || window.entitlements || {})) return false
  return !isAppsumoReviewDismissed(getAppsumoReview(data))
}

export const snoozeUntilIso = (days = SNOOZE_DAYS) =>
  new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString()

export const persistAppsumoReview = (updateUser, currentData, patch, { refetch = true } = {}) => {
  const appsumo_review = mergeAppsumoReview(currentData, patch)
  const nextData = { ...(currentData || {}), [APPSUMO_REVIEW_KEY]: appsumo_review }
  window.shouldShowAppSumoReviewWizard = computeShouldShow({
    entitlements: window.entitlements,
    data: nextData,
  })

  return updateUser({
    variables: {
      input: {
        data: { [APPSUMO_REVIEW_KEY]: appsumo_review },
      },
    },
    refetchQueries: refetch ? ['me'] : undefined,
    awaitRefetchQueries: refetch || undefined,
  })
}

const readCurrentUser = async () => {
  try {
    const cached = client.readQuery({ query: CURRENT_USER })
    if (cached?.me) return cached.me
  } catch (_) { /* cache miss */ }

  const { data } = await client.query({ query: CURRENT_USER, fetchPolicy: 'cache-first' })
  return data?.me
}

/**
 * Mark AppSumo "got value" after any campaign activation (email, widget,
 * landing page, drip, automation). No-ops if already marked, dismissed, or ineligible.
 *
 * @param {boolean} openWizard - When true (broadcast/autoresponder only), also set
 *   pending_wizard so Campaigns auto-opens the review modal. Other campaigns leave
 *   this false so only the banner shows.
 */
export const markAppsumoGotValue = async ({ refetch = true, openWizard = false } = {}) => {
  try {
    if (!isEligibleAppsumoLicense(window.entitlements || {})) return

    const me = await readCurrentUser()
    const entitlements = me?.entitlements || window.entitlements
    if (!shouldMarkGotValueAfterSend({ entitlements, data: me?.data })) return
    if (hasAppsumoGotValue(getAppsumoReview(me?.data))) return

    await persistAppsumoReview(
      (opts) => client.mutate({ mutation: UPDATE_USER, ...opts }),
      me?.data,
      {
        got_value: true,
        ...(openWizard ? { pending_wizard: true } : {}),
      },
      { refetch },
    )
  } catch (err) {
    console.error(err)
  }
}
